import React from 'react';
import type { TFunction } from 'i18next';
import {
  type AccountData,
  type AccountLocationData,
  type EventScheduleData,
  type EventScheduleSlotData,
  type Language,
  type PartialLabelData,
  type PropelUserModel,
  WorkDayType,
  type WorkingDaysData,
  type WorkingHoursData,
} from '../../api';
import type { ChangeEntry } from './SaveHeaderContext';
import { mb } from '../../common/theme/mbTokens';
import type { EventScheduleDay } from '../eventSchedule/EventSchedule';
import { htmlValuesDiffer, renderRichDiffPair, renderTextDiffPair } from './richTextDiff';

// Field diff builders. Each helper compares two values, and if they differ,
// returns a fully-formed ChangeEntry ready to push into the changes array.
// Returns null when values are equivalent so callers can filter.
//
// Convention: builders read like `diffField(label, before, after, ...deps)`.
// Sections compose them into a `getChanges` — no glue code, no raw ids.

export type ChangeBuilder = ChangeEntry | null;

const eq = (a: unknown, b: unknown) => a === b || (a == null && b == null);

export const diffText = (label: string, before?: string, after?: string): ChangeBuilder => {
  if (eq(before, after)) return null;
  return { label, ...renderTextDiffPair(before, after) };
};

// For enum-like values whose UI label is a translation, not the raw key. Pass a
// resolver that turns the raw value into a display string (e.g., via t()).
export const diffEnum = <V extends string | number | undefined>(
  label: string,
  before: V,
  after: V,
  resolve: (v: V) => string,
): ChangeBuilder => {
  if (eq(before, after)) return null;
  return { label, from: resolve(before) || '—', to: resolve(after) || '—' };
};

export const diffRichText = (label: string, before?: string, after?: string): ChangeBuilder => {
  if (!htmlValuesDiffer(before, after)) return null;
  return { label, ...renderRichDiffPair(before, after) };
};

export const diffNumber = (
  label: string,
  before: number | undefined | null,
  after: number | undefined | null,
  opts: { suffix?: string; prefix?: string } = {},
): ChangeBuilder => {
  if (eq(before, after)) return null;
  const fmt = (v: number | undefined | null) => (v == null ? '—' : `${opts.prefix ?? ''}${v}${opts.suffix ?? ''}`);
  return { label, from: fmt(before), to: fmt(after) };
};

export const diffBoolean = (
  label: string,
  before: boolean | undefined,
  after: boolean | undefined,
  _t?: TFunction,
): ChangeBuilder => {
  if (eq(!!before, !!after)) return null;
  return { label, from: before ? '✔' : '✘', to: after ? '✔' : '✘' };
};

export const diffCount = (
  label: string,
  before: number | undefined,
  after: number | undefined,
  t: TFunction,
): ChangeBuilder => {
  const b = before ?? 0;
  const a = after ?? 0;
  if (b === a) return null;
  const fmt = (n: number) => t('saveHeader.itemsCount', { count: n });
  return { label, from: fmt(b), to: fmt(a) };
};

// Resolves a location id to its address label, then diffs against the previous
// address label. Displays "—" if the id is missing / unknown.
export const diffLocation = (
  label: string,
  before: string | undefined,
  after: string | undefined,
  account: AccountData,
  language: string,
): ChangeBuilder => {
  if (eq(before, after)) return null;
  const resolve = (id?: string): string => {
    if (!id) return '';
    const loc = account.locations.find((l: AccountLocationData) => l.id === id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (loc?.address as any)?.[language] || loc?.id || '';
  };
  return { label, ...renderTextDiffPair(resolve(before), resolve(after)) };
};

export const diffDurationMinutes = (
  label: string,
  before: number | undefined,
  after: number | undefined,
  t: TFunction,
): ChangeBuilder => {
  if (eq(before, after)) return null;
  const fmt = (v: number | undefined) => (v == null ? '—' : t('saveHeader.minutes', { count: v }));
  return { label, from: fmt(before), to: fmt(after) };
};

// Time-of-day as HH:MM (24h).
export const diffTimeOfDay = (
  label: string,
  before: { hour?: number; minute?: number } | undefined,
  after: { hour?: number; minute?: number } | undefined,
): ChangeBuilder => {
  const fmt = (v: { hour?: number; minute?: number } | undefined) =>
    v?.hour == null ? '—' : `${String(v.hour).padStart(2, '0')}:${String(v.minute ?? 0).padStart(2, '0')}`;
  const b = fmt(before);
  const a = fmt(after);
  if (b === a) return null;
  return { label, from: b, to: a };
};

// Time-of-day stored as seconds since midnight (as FormTimePicker persists).
export const diffTimeOfDaySeconds = (
  label: string,
  before: number | undefined,
  after: number | undefined,
): ChangeBuilder => {
  if (eq(before, after)) return null;
  const fmt = (secs: number | undefined) => {
    if (secs == null) return '—';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  return { label, from: fmt(before), to: fmt(after) };
};

// Diff a list of per-language label items (as edited via PartialLabelListDrawer).
// Emits one row per changed language per item — additions/removals/edits — so a
// Hebrew-only tweak on item #2 shows up instead of hiding behind an unchanged count.
export const diffPartialLabelList = (
  label: string,
  before: PartialLabelData[] | undefined,
  after: PartialLabelData[] | undefined,
  languages: Language[],
  t: TFunction,
): ChangeEntry[] => {
  const entries: ChangeEntry[] = [];
  const b = before || [];
  const a = after || [];
  const max = Math.max(b.length, a.length);
  for (let i = 0; i < max; i += 1) {
    const bItem = b[i];
    const aItem = a[i];
    languages.forEach((lang) => {
      const bl = bItem?.[lang];
      const al = aItem?.[lang];
      if (bl === al) return;
      entries.push({
        label: `${label} #${i + 1} (${t(`emailTemplates.${lang}`)})`,
        ...renderTextDiffPair(bl, al),
      });
    });
  }
  return entries;
};

const addedChipStyle: React.CSSProperties = {
  backgroundColor: mb.mint,
  color: mb.green,
  fontWeight: 600,
  padding: '0 6px',
  borderRadius: 4,
  marginInlineEnd: 4,
  display: 'inline-block',
};
const removedChipStyle: React.CSSProperties = {
  backgroundColor: mb.dangerSoft,
  color: mb.danger,
  textDecoration: 'line-through',
  padding: '0 6px',
  borderRadius: 4,
  marginInlineEnd: 4,
  display: 'inline-block',
};

// Diff a multi-select of ids (e.g. parallel order pages, redeemable-on items).
// Deltas only: Before shows removed items (red + strike), After shows added
// items (green + bold). Kept items are omitted from both sides.
export const diffIdList = <ID extends string | number>(
  label: string,
  before: ID[] | undefined,
  after: ID[] | undefined,
  resolve: (id: ID) => string,
): ChangeBuilder => {
  const beforeArr = before || [];
  const afterArr = after || [];
  const afterSet = new Set<ID>(afterArr);
  const beforeSet = new Set<ID>(beforeArr);
  const removed = beforeArr.filter((id) => !afterSet.has(id));
  const added = afterArr.filter((id) => !beforeSet.has(id));
  if (removed.length === 0 && added.length === 0) return null;

  const chips = (ids: ID[], style: React.CSSProperties, keyPrefix: string) =>
    ids.length === 0 ? (
      '—'
    ) : (
      <>
        {ids.map((id) => (
          <span key={`${keyPrefix}-${id}`} style={style}>
            {resolve(id) || String(id)}
          </span>
        ))}
      </>
    );

  return {
    label,
    from: chips(removed, removedChipStyle, 'r'),
    to: chips(added, addedChipStyle, 'a'),
  };
};

const secondsToHHMM = (secs?: number): string => {
  if (secs == null) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const workDayTypeLabel = (t: TFunction, typ?: WorkDayType): string => {
  if (typ === WorkDayType.NOT_WORKING) return t('workingDays.notWorking');
  if (typ === WorkDayType.FIXED) return t('workingDays.fixed');
  if (typ === WorkDayType.INTERVAL) return t('workingDays.interval');
  return '';
};

const timesForDay = (h?: WorkingHoursData): string[] => {
  if (!h) return [];
  if (h.work_day_type === WorkDayType.INTERVAL) {
    return h.from_time != null || h.to_time != null
      ? [`${secondsToHHMM(h.from_time)} – ${secondsToHHMM(h.to_time)}`]
      : [];
  }
  if (h.work_day_type === WorkDayType.FIXED) {
    return (h.start_seconds || []).map(secondsToHHMM);
  }
  return [];
};

const chipRow = (chips: { text: string; style: React.CSSProperties }[], keyPrefix: string): React.ReactNode => {
  if (chips.length === 0) return '—';
  return (
    <>
      {chips.map((c, i) => (
        <span key={`${keyPrefix}-${i}`} style={c.style}>
          {c.text}
        </span>
      ))}
    </>
  );
};

// Per-day working-hours diff. Handles three cases:
// 1. work_day_type changed → old type + old times crossed out, new type + new times in green
// 2. INTERVAL times changed → old range crossed out, new range in green
// 3. FIXED slots changed → removed slots crossed out, added slots in green (kept slots omitted)
export const diffWorkingDays = (
  before: WorkingDaysData | undefined,
  after: WorkingDaysData | undefined,
  t: TFunction,
): ChangeEntry[] => {
  const days: (keyof WorkingDaysData)[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];
  const entries: ChangeEntry[] = [];
  days.forEach((d) => {
    const b = before?.[d];
    const a = after?.[d];
    const label = t(`days.${d}`);
    const typeChanged = b?.work_day_type !== a?.work_day_type;

    if (typeChanged) {
      const beforeChips: { text: string; style: React.CSSProperties }[] = [];
      const afterChips: { text: string; style: React.CSSProperties }[] = [];
      if (b?.work_day_type) beforeChips.push({ text: workDayTypeLabel(t, b.work_day_type), style: removedChipStyle });
      timesForDay(b).forEach((tt) => beforeChips.push({ text: tt, style: removedChipStyle }));
      if (a?.work_day_type) afterChips.push({ text: workDayTypeLabel(t, a.work_day_type), style: addedChipStyle });
      timesForDay(a).forEach((tt) => afterChips.push({ text: tt, style: addedChipStyle }));
      entries.push({ label, from: chipRow(beforeChips, `${d}-r`), to: chipRow(afterChips, `${d}-a`) });
      return;
    }

    if (b?.work_day_type === WorkDayType.INTERVAL) {
      if (b?.from_time !== a?.from_time || b?.to_time !== a?.to_time) {
        entries.push({
          label,
          from: chipRow(
            [{ text: `${secondsToHHMM(b?.from_time)} – ${secondsToHHMM(b?.to_time)}`, style: removedChipStyle }],
            `${d}-r`,
          ),
          to: chipRow(
            [{ text: `${secondsToHHMM(a?.from_time)} – ${secondsToHHMM(a?.to_time)}`, style: addedChipStyle }],
            `${d}-a`,
          ),
        });
      }
      return;
    }

    if (b?.work_day_type === WorkDayType.FIXED) {
      const beforeSet = new Set(b?.start_seconds || []);
      const afterSet = new Set(a?.start_seconds || []);
      const removed = (b?.start_seconds || []).filter((s) => !afterSet.has(s));
      const added = (a?.start_seconds || []).filter((s) => !beforeSet.has(s));
      if (removed.length || added.length) {
        entries.push({
          label,
          from: chipRow(
            removed.map((s) => ({ text: secondsToHHMM(s), style: removedChipStyle })),
            `${d}-r`,
          ),
          to: chipRow(
            added.map((s) => ({ text: secondsToHHMM(s), style: addedChipStyle })),
            `${d}-a`,
          ),
        });
      }
    }
  });
  return entries;
};

const formatSlot = (slot: EventScheduleSlotData, users: PropelUserModel[]): string => {
  const time = secondsToHHMM(slot.start_time);
  const ids = slot.employee_ids || [];
  if (ids.length === 0) return time;
  const names = ids
    .map((id) => {
      const u = users.find((x) => x.user_id === id);
      return u ? `${u.first_name} ${u.last_name}` : id;
    })
    .join(', ');
  return `${time} (${names})`;
};

const eventSlotsEqual = (a: EventScheduleSlotData, b: EventScheduleSlotData): boolean => {
  if (a.start_time !== b.start_time) return false;
  const aIds = [...(a.employee_ids || [])].sort();
  const bIds = [...(b.employee_ids || [])].sort();
  if (aIds.length !== bIds.length) return false;
  return aIds.every((id, i) => id === bIds[i]);
};

// Per-day event-schedule diff. Slots are keyed by start_time (validation forbids
// duplicates in one day). Removed and added slots show as red-strike / green
// chips; a slot whose employee list changed shows on both sides so the manager
// can see the swap.
export const diffEventSchedule = (
  before: EventScheduleData | undefined,
  after: EventScheduleData | undefined,
  users: PropelUserModel[],
  t: TFunction,
): ChangeEntry[] => {
  const days: EventScheduleDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const entries: ChangeEntry[] = [];
  days.forEach((d) => {
    const b = before?.[d] || [];
    const a = after?.[d] || [];
    const byTime = (arr: EventScheduleSlotData[]) => new Map(arr.map((s) => [s.start_time, s]));
    const bMap = byTime(b);
    const aMap = byTime(a);

    const beforeChips: { text: string; style: React.CSSProperties }[] = [];
    const afterChips: { text: string; style: React.CSSProperties }[] = [];

    b.forEach((slot) => {
      const match = aMap.get(slot.start_time);
      if (!match) {
        beforeChips.push({ text: formatSlot(slot, users), style: removedChipStyle });
      } else if (!eventSlotsEqual(slot, match)) {
        beforeChips.push({ text: formatSlot(slot, users), style: removedChipStyle });
      }
    });
    a.forEach((slot) => {
      const match = bMap.get(slot.start_time);
      if (!match) {
        afterChips.push({ text: formatSlot(slot, users), style: addedChipStyle });
      } else if (!eventSlotsEqual(slot, match)) {
        afterChips.push({ text: formatSlot(slot, users), style: addedChipStyle });
      }
    });

    if (beforeChips.length === 0 && afterChips.length === 0) return;
    entries.push({
      label: t(`days.${d}`),
      from: chipRow(beforeChips, `${d}-r`),
      to: chipRow(afterChips, `${d}-a`),
    });
  });
  return entries;
};

type ChangeInput = ChangeBuilder | ChangeEntry[];

// Utility: collect non-null builder results into a flat ChangeEntry[]. Accepts
// both single ChangeBuilder returns and ChangeEntry[] arrays (from multi-row
// builders like diffPartialLabelList).
export const collect = (...inputs: ChangeInput[]): ChangeEntry[] =>
  inputs.flatMap((c) => {
    if (c === null) return [];
    if (Array.isArray(c)) return c;
    return [c];
  });
